import { useEffect, useMemo, useState } from 'react'
import type {
    AgenteMonedaView,
    ClienteDTO,
    KeyValue,
    PagaebleResult,
    SearchAseguradoraDTO,
    SearchPolizaDTO,
    SearchVendedorDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import {
    ALL_ROWS,
    breakdown,
    catalogLabel,
    defaultRange,
    downloadCsv,
    errorMessage,
    formatAmount,
    formatSlashDate,
    formatSlashDateTime,
    loadCatalog,
    matchesSearch,
    PAGE_SIZES,
    pageWindow,
    paginate,
    personName,
    totalLabel,
} from '../common/report-common'
import type { BreakdownDimension, ReportColumn } from '../common/report-common'

export const RAMOS = ['Vida', 'Auto', 'Salud', 'Diversos']
const LOAD_ERROR = 'No se pudo cargar el reporte de pólizas.'
const RANGE_ERROR = 'La fecha final debe ser igual o posterior a la inicial.'

export interface PolicyReportFilters {
    vigenciaDe: string
    vigenciaA: string
    status: string
    ramo: string
    formaPago: string
    moneda: string
    aseguradoraId: string
    cliente: ClienteDTO | null
    vendedor: SearchVendedorDTO | null
}

export interface PolicyReportRow {
    id: number
    noPoliza: string
    contratante: string
    concepto: string
    aseguradora: string
    clave: string
    ramo: string
    status: string
    formaPago: string
    vigenciaDe: string
    vigenciaA: string
    creacion: string
    prima: number
    total: number
    moneda: string
    vendedor: string
    robot: boolean
}

const defaultFilters = (): PolicyReportFilters => {
    const range = defaultRange()
    return {
        vigenciaDe: range.from,
        vigenciaA: range.to,
        status: '',
        ramo: '',
        formaPago: '',
        moneda: '',
        aseguradoraId: '',
        cliente: null,
        vendedor: null,
    }
}

const buildQuery = (filters: PolicyReportFilters) => {
    const params = [`page=1`, `size=${ALL_ROWS}`]
    if (filters.vigenciaDe) params.push(`fechaVigenciaDe[ge]=${filters.vigenciaDe}`)
    if (filters.vigenciaA) params.push(`fechaVigenciaDe[le]=${filters.vigenciaA}`)
    if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`)
    if (filters.ramo) params.push(`ramo=${encodeURIComponent(filters.ramo)}`)
    if (filters.formaPago) params.push(`formaPago=${encodeURIComponent(filters.formaPago)}`)
    if (filters.moneda) params.push(`moneda=${encodeURIComponent(filters.moneda)}`)
    if (filters.aseguradoraId) params.push(`aseguradoraId=${filters.aseguradoraId}`)
    if (filters.cliente?.id) params.push(`clienteId=${filters.cliente.id}`)
    if (filters.vendedor?.id) params.push(`vendedorId=${filters.vendedor.id}`)
    return params.join('&')
}

const toRow = (item: SearchPolizaDTO, statuses: KeyValue[], formas: KeyValue[]): PolicyReportRow => ({
    id: item.polizaId,
    noPoliza: String(item.noPoliza || '').toUpperCase(),
    contratante: personName(item),
    concepto: item.concepto || '',
    aseguradora: item.aseguradora || '',
    clave: item.agenteClave || '',
    ramo: item.ramo || '',
    status: catalogLabel(statuses, item.status),
    formaPago: catalogLabel(formas, item.formaPago),
    vigenciaDe: formatSlashDate(item.fechaVigenciaDe) || '',
    vigenciaA: formatSlashDate(item.fechaVigenciaA) || '',
    creacion: formatSlashDateTime(item.fecha) || '',
    prima: Number(item.prima) || 0,
    total: Number(item.total) || 0,
    moneda: item.moneda || 'MXN',
    vendedor: personName({ nombres: item.vendedorNombres, apPat: item.vendedorApPat, apMat: item.vendedorApMat }),
    robot: Boolean(item.esRobot),
})

export const CSV_COLUMNS: ReportColumn<PolicyReportRow>[] = [
    { key: 'noPoliza', label: 'No. póliza', csv: (row) => row.noPoliza },
    { key: 'contratante', label: 'Contratante', csv: (row) => row.contratante },
    { key: 'concepto', label: 'Concepto', csv: (row) => row.concepto },
    { key: 'aseguradora', label: 'Aseguradora', csv: (row) => row.aseguradora },
    { key: 'clave', label: 'Clave', csv: (row) => row.clave },
    { key: 'ramo', label: 'Ramo', csv: (row) => row.ramo },
    { key: 'status', label: 'Estatus', csv: (row) => row.status },
    { key: 'formaPago', label: 'Forma de pago', csv: (row) => row.formaPago },
    { key: 'vigenciaDe', label: 'Vigencia de', csv: (row) => row.vigenciaDe },
    { key: 'vigenciaA', label: 'Vigencia a', csv: (row) => row.vigenciaA },
    { key: 'creacion', label: 'Fecha de creación', csv: (row) => row.creacion },
    { key: 'prima', label: 'Prima neta', csv: (row) => row.prima.toFixed(2) },
    { key: 'total', label: 'Prima total', csv: (row) => row.total.toFixed(2) },
    { key: 'moneda', label: 'Moneda', csv: (row) => row.moneda },
    { key: 'vendedor', label: 'Vendedor', csv: (row) => row.vendedor },
    { key: 'robot', label: 'Robot', csv: (row) => (row.robot ? 'Sí' : 'No') },
]

export const useReportPoliciesModel = () => {
    const [filters, setFilters] = useState<PolicyReportFilters>(defaultFilters)
    const [query, setQuery] = useState(() => buildQuery(defaultFilters()))
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<SearchPolizaDTO[]>([])
    const [statuses, setStatuses] = useState<KeyValue[]>([])
    const [formas, setFormas] = useState<KeyValue[]>([])
    const [monedas, setMonedas] = useState<AgenteMonedaView[]>([])
    const [aseguradoras, setAseguradoras] = useState<SearchAseguradoraDTO[]>([])
    const [dimension, setDimension] = useState<BreakdownDimension>('ramo')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [detailId, setDetailId] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const loadCatalogs = async () => {
            try {
                const [statusList, formaList, monedaResponse, aseguradoraResponse] = await Promise.all([
                    loadCatalog('/catalogos/polizas/status'),
                    loadCatalog('/catalogos/forma-pago'),
                    APIInvoke.invokeGET('/monedas').then((response) => response.json() as Promise<WrapperResponse<AgenteMonedaView[]>>),
                    APIInvoke.invokeGET(`/aseguradoras?size=${ALL_ROWS}&order=aseguradora asc`).then((response) => response.json() as Promise<WrapperResponse<PagaebleResult<SearchAseguradoraDTO>>>),
                ])
                if (!active) return
                setStatuses(statusList)
                setFormas(formaList)
                setMonedas(monedaResponse?.ok ? monedaResponse.body || [] : [])
                setAseguradoras(aseguradoraResponse?.ok ? aseguradoraResponse.body?.items || [] : [])
            } catch (catalogError) {
                console.error(catalogError)
            }
        }
        loadCatalogs()
        return () => { active = false }
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const payload: WrapperResponse<PagaebleResult<SearchPolizaDTO>> | null = await (await APIInvoke.invokeGET(`/polizas?${query}`, { timeoutMs: LONG_TIMEOUT_MS })).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setPage(1)
            } catch (loadError) {
                if (active) {
                    setItems([])
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [query, reloadToken])

    const rangeInvalid = Boolean(filters.vigenciaDe && filters.vigenciaA && filters.vigenciaDe > filters.vigenciaA)

    const apply = (next: PolicyReportFilters) => {
        setFilters(next)
        const invalid = Boolean(next.vigenciaDe && next.vigenciaA && next.vigenciaDe > next.vigenciaA)
        if (!invalid) setQuery(buildQuery(next))
    }

    const change = <K extends keyof PolicyReportFilters>(key: K, value: PolicyReportFilters[K]) => apply({ ...filters, [key]: value })

    const allRows = useMemo(() => items.map((item) => toRow(item, statuses, formas)), [items, statuses, formas])
    const filteredRows = useMemo(
        () => allRows.filter((row) => matchesSearch(search, row.noPoliza, row.contratante, row.concepto, row.aseguradora, row.clave, row.vendedor)),
        [allRows, search],
    )
    const paged = paginate(filteredRows, page, pageSize)

    const breakdowns = useMemo(() => ({
        ramo: breakdown(allRows, (row) => row.ramo, (row) => row.total),
        status: breakdown(allRows, (row) => row.status, (row) => row.total),
        aseguradora: breakdown(allRows, (row) => row.aseguradora, (row) => row.total),
    }), [allRows])

    const totals = totalLabel(allRows, (row) => row.total, (row) => row.moneda)
    const netTotals = totalLabel(allRows, (row) => row.prima, (row) => row.moneda)

    return {
        filters,
        rangeInvalid,
        rangeError: rangeInvalid ? RANGE_ERROR : null,
        statuses,
        formas,
        monedas,
        aseguradoras,
        loading,
        error,
        count: allRows.length,
        totals,
        netTotals,
        robots: allRows.filter((row) => row.robot).length,
        dimension,
        breakdown: breakdowns[dimension],
        search,
        rows: paged.rows,
        filteredCount: filteredRows.length,
        page: paged.page,
        totalPages: paged.totalPages,
        pages: filteredRows.length > 0 ? pageWindow(paged.page, paged.totalPages) : [],
        pageSize,
        rangeFrom: paged.rangeFrom,
        rangeTo: paged.rangeTo,
        detailId,
        formatSum: (value: number) => `$${formatAmount(value)}`,
        change,
        clearFilters: () => apply(defaultFilters()),
        changeDimension: setDimension,
        changeSearch: (value: string) => {
            setSearch(value)
            setPage(1)
        },
        setPage,
        changePageSize: (value: number) => {
            setPageSize(value)
            setPage(1)
        },
        exportCsv: () => downloadCsv('reporte-polizas', CSV_COLUMNS, filteredRows),
        reload: () => setReloadToken((token) => token + 1),
        openPoliza: (id: number) => setDetailId(id),
        closePoliza: () => setDetailId(null),
    }
}
